import React from 'react';
import { getTpmsTireColor, TireStatus } from 'utils/tpms';
import TireTooltip from 'components/Tire/TireTooltip';
import BatteryIndicator from 'components/Tire/BatteryIndicator';
import type { TireInfo } from 'components/Tire/TireTooltip';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';
import truckImage from 'assets/vehicles/haul-truck-10x6.png';

interface TireAlert {
  icon: string;
  color: string;
}

interface TireData {
  p1?: TireInfo;
  p2?: TireInfo;
  p3?: TireInfo;
  p4?: TireInfo;
  p5?: TireInfo;
  p6?: TireInfo;
  p7?: TireInfo;
  p8?: TireInfo;
  p9?: TireInfo;
  p10?: TireInfo;
}

// Tri-axle haul truck (10x6): front single (p1/p2), two rear tandem dual
// axles (p3-p6, p7-p10). Background is the same source photo GraderSixWheels
// embeds (extracted from its base64 fill, rotated upright — front axle at
// top), used the same way: a faint (10% opacity) decorative backdrop, not
// pixel-aligned to real wheels (GraderSixWheels itself doesn't align its 6
// markers to this photo's actual wheels either). Marker positions are
// percentages of the 1331x2000 photo. p-index -> tireCode order matches
// views/TirePressure/util.ts#getOrderedTireCodes for patternName '10x6'.
//
// The viewBox is taller than the photo itself (2000px) — the 5 stacked
// gauge cards per side run noticeably taller than the photo's own aspect
// ratio would render at, so the extra canvas height lets the axle rows (and
// their dogleg connector lines) spread out enough to actually reach each
// card's real position instead of compressing into the photo's shorter
// height. The photo stays undistorted, top-anchored, with empty canvas
// below it.
const IMG_W = 1331;
const IMG_H = 3200;
const PHOTO_H = 2000;

// `cardOrder` is this tire's index (0-4) within its side's 5-card stack in
// TenTires.tsx (left column: p1,p3,p5,p7,p9 top-to-bottom; right: p2,p4,p6,
// p8,p10) — used to target each connector line at that card's approximate
// vertical position, since the diagram only has 3 physical axle rows but
// each side has 5 stacked cards.
const MARKERS: Array<{
  key: keyof TireData;
  x: number;
  y: number;
  cardOrder: number;
}> = [
  { key: 'p1', x: 30, y: 13, cardOrder: 0 },
  { key: 'p2', x: 70, y: 13, cardOrder: 0 },
  { key: 'p3', x: 32, y: 55, cardOrder: 1 },
  { key: 'p4', x: 68, y: 55, cardOrder: 1 },
  { key: 'p5', x: 15, y: 55, cardOrder: 2 },
  { key: 'p6', x: 85, y: 55, cardOrder: 2 },
  { key: 'p7', x: 32, y: 72, cardOrder: 3 },
  { key: 'p8', x: 68, y: 72, cardOrder: 3 },
  { key: 'p9', x: 15, y: 72, cardOrder: 4 },
  { key: 'p10', x: 85, y: 72, cardOrder: 4 },
];

// 5 cards stacked per side — target each line at that card's approximate
// vertical center (assuming the card column roughly spans the diagram's
// height).
const CARD_COUNT = 5;
const cardTargetY = (order: number) => ((order + 0.5) / CARD_COUNT) * IMG_H;

// Tire body size — same proportions as GraderSixWheels' tire rects
// (40x98 in its 492x955 viewBox, i.e. ~8.1% x ~10.3%), scaled to this
// component's 357x846 viewBox, so the tandem-dual layout reads at the same
// relative size as the existing 6x6 diagram.
// All of these are scaled against PHOTO_H (the photo's own height), not the
// taller IMG_H canvas — the axle rows and tires sit on the photo itself, so
// they need to stay within its actual bounds regardless of how much extra
// empty canvas is added below for the dogleg lines to reach into.
const TIRE_W = (40 / 492) * IMG_W;
const TIRE_H = (98 / 955) * PHOTO_H;
const DOT_R = 7.2;

// Axle/frame crossbars, matching the drawn chassis convention those same
// components use (a light bar per axle plus a center spine) rather than
// relying on the photo's own faint frame rail. Thickness matches
// GraderSixWheels' 12px bar scaled the same way (12/955 of viewBox height).
const AXLE_COLOR = '#DFDFDF';
const AXLE_WIDTH = (12 / 955) * PHOTO_H;
const FRONT_Y = (13 / 100) * PHOTO_H;
const AXLE2_Y = (55 / 100) * PHOTO_H;
const AXLE3_Y = (72 / 100) * PHOTO_H;
const CENTER_X = IMG_W / 2;
const FRONT_LEFT_X = (30 / 100) * IMG_W;
const FRONT_RIGHT_X = (70 / 100) * IMG_W;
const OUTER_LEFT_X = (15 / 100) * IMG_W;
const OUTER_RIGHT_X = (85 / 100) * IMG_W;

interface TenWheelHaulTruckProps {
  values: any;
  name?: string;
  tireData?: TireData;
  batteryLow?: Record<string, boolean>;
  tireStatusMap?: Record<string, TireStatus>;
  tireStaleMap?: Record<string, boolean>;
  tireAlertMap?: Record<string, TireAlert>;
  onTireClick?: (tireCode: string) => void;
  tireNumberSystem?: string[];
  width?: number | string;
  height?: number | string;
}

const TenWheelHaulTruck: React.FC<TenWheelHaulTruckProps> = ({
  values,
  tireData,
  batteryLow,
  tireStatusMap,
  tireStaleMap,
  tireAlertMap,
  onTireClick,
  tireNumberSystem,
  width = '100%',
  height,
}) => {
  const palette = clientThemePalette[CLIENT_THEME].light;

  const renderTireAlert = (key: string, cx: number, cy: number) => {
    const alert = tireAlertMap?.[key];
    if (!alert) return null;

    const isPlayIcon = alert.icon.includes('▶');
    const fontSize = isPlayIcon ? (alert.icon.length > 1 ? 5 : 8) : 10;

    return (
      <g style={{ pointerEvents: 'none' }}>
        <circle
          cx={cx}
          cy={cy}
          r={DOT_R * 0.7}
          fill={alert.color}
          stroke="#FFFFFF"
          strokeWidth={1.2}
        />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#FFFFFF"
          fontSize={fontSize}
          fontWeight="900"
        >
          {alert.icon}
        </text>
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={height}
      style={height === undefined ? { width, height: 'auto' } : undefined}
      viewBox={`0 0 ${IMG_W} ${IMG_H}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <image
        href={truckImage}
        xlinkHref={truckImage}
        x={0}
        y={0}
        width={IMG_W}
        height={PHOTO_H}
        opacity={0.1}
        style={{ pointerEvents: 'none' }}
        preserveAspectRatio="xMidYMin meet"
      />

      {/* Axle/frame crossbars, matching the drawn-chassis convention used by
          FourWheels/SixWheel/GraderSixWheels. */}
      <line
        x1={CENTER_X}
        y1={FRONT_Y}
        x2={CENTER_X}
        y2={AXLE3_Y}
        stroke={AXLE_COLOR}
        strokeWidth={AXLE_WIDTH}
      />
      <line
        x1={FRONT_LEFT_X}
        y1={FRONT_Y}
        x2={FRONT_RIGHT_X}
        y2={FRONT_Y}
        stroke={AXLE_COLOR}
        strokeWidth={AXLE_WIDTH}
      />
      <line
        x1={OUTER_LEFT_X}
        y1={AXLE2_Y}
        x2={OUTER_RIGHT_X}
        y2={AXLE2_Y}
        stroke={AXLE_COLOR}
        strokeWidth={AXLE_WIDTH}
      />
      <line
        x1={OUTER_LEFT_X}
        y1={AXLE3_Y}
        x2={OUTER_RIGHT_X}
        y2={AXLE3_Y}
        stroke={AXLE_COLOR}
        strokeWidth={AXLE_WIDTH}
      />

      {/* Connector lines out to the side gauge cards — an L-shaped dogleg
          (vertical then horizontal) per marker, matching the same technique
          SixWheel.tsx uses, since the diagram only has 3 axle rows but each
          side stacks 5 cards at 5 different heights. */}
      {MARKERS.map(({ key, x, y, cardOrder }) => {
        const cx = (x / 100) * IMG_W;
        const cy = (y / 100) * PHOTO_H;
        const isLeft = x < 50;
        const targetY = cardTargetY(cardOrder);
        return (
          <g key={`line-${key}`} style={{ pointerEvents: 'none' }}>
            <line
              x1={cx}
              y1={cy}
              x2={cx}
              y2={targetY}
              stroke="#CCCCCC"
              strokeWidth={1}
            />
            <line
              x1={cx}
              y1={targetY}
              x2={isLeft ? 0 : IMG_W}
              y2={targetY}
              stroke="#CCCCCC"
              strokeWidth={1}
            />
          </g>
        );
      })}

      {MARKERS.map(({ key, x, y }, index) => {
        const cx = (x / 100) * IMG_W;
        const cy = (y / 100) * PHOTO_H;
        const status =
          tireStatusMap?.[key] ??
          values?.[`${key}Status`] ??
          TireStatus.NO_SENSOR;
        const isStale =
          tireStaleMap?.[key] ?? values?.[`${key}IsStale`] ?? false;
        const color = getTpmsTireColor(status, isStale);
        const code = tireNumberSystem?.[index];

        return (
          <TireTooltip key={key} tireInfo={tireData?.[key]}>
            <g
              onClick={() => {
                if (onTireClick && code) onTireClick(code);
              }}
              style={{ cursor: onTireClick ? 'pointer' : 'default' }}
            >
              <rect
                x={cx - TIRE_W / 2}
                y={cy - TIRE_H / 2}
                width={TIRE_W}
                height={TIRE_H}
                rx={(8 / 40) * TIRE_W}
                fill={color}
                fillOpacity={1}
              />
              <circle
                cx={cx}
                cy={cy}
                r={DOT_R}
                fill={palette.primary}
                stroke="#FFFFFF"
                strokeWidth={0.8}
              />
              <BatteryIndicator
                cx={cx}
                cy={cy - TIRE_H / 2}
                tireBottomY={cy}
                tireWidth={TIRE_W}
                show={batteryLow?.[key]}
              />
              {renderTireAlert(key, cx, cy)}
            </g>
          </TireTooltip>
        );
      })}
    </svg>
  );
};

export default TenWheelHaulTruck;
